import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';

import { createCashTableConfig, TableRowAction } from './cash-table.config';
import { CashListSummary, CashReadService, EMPTY_CASH_SUMMARY } from '../../services/cash-read.service';

import { Utils } from '../../../../../shared/utils';
import { EfectivoService } from '../../../../../services/finanzas/efectivo/efectivo.service';
import { Efectivo } from '../../../../../models/finanzas/efectivo/efectivo.model';
import { CashFormDialogComponent } from '../cash-form-dialog/cash-form-dialog.component';
import { EfectivoDialogIngresoACajaComponent } from '../../../../../modules/finanzas/efectivo/efectivo-dialog-ingreso-a-caja.component';
import { EfectivoDialogMovimientoSocioComponent } from '../../../../../modules/finanzas/efectivo/efectivo-dialog-movimiento-socio.component';
import { AccountingAccountsService } from '../../../../../modules/accounting/accounting-accounts/accounting-accounts.service';
import { AccountingAccountsModel } from '../../../../../modules/accounting/accounting-accounts/accounting-accounts.model';
import { AccountingAccountsDialogLedgerComponent } from '../../../../../modules/accounting/accounting-accounts/accounting-accounts-dialog-ledger.component';

type CashRow = Efectivo & { __expanded?: boolean };

@Component({
  standalone: false,
  selector: 'app-cash-list-v2',
  templateUrl: './cash-list.components.html',
  styleUrls: ['./cash-list.components.css']
})
export class CashListComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('cashSummary') cashSummaryTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;

  icon = 'account_balance_wallet';
  title = 'Caja / Efectivo';
  subTitle = 'Consulta, edita o gestiona tus cajas de efectivo';

  summary: CashListSummary = { ...EMPTY_CASH_SUMMARY };

  quickSearch = '';
  private readonly quickSearch$ = new Subject<string>();

  private rows: CashRow[] = [];
  private lastState: TableStateSnapshot | null = null;
  private loadSeq = 0;

  private destroy$ = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private reads: CashReadService,
    private efectivoService: EfectivoService,
    private accountingAccountsService: AccountingAccountsService,
    private loadingDialog: LoadingDialogService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private tier: TierService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.tableConfig = createCashTableConfig(
      {
        actionMap: {
          [TableRowAction.Edit]: (row) => this.edit(row),
          [TableRowAction.Delete]: (row) => this.delete(row),
          [TableRowAction.IngresoBanco]: (row) => this.ingresodebanco(row),
          [TableRowAction.RetiroSocio]: (row) => this.movimientosocio(row, 1),
          [TableRowAction.IngresoSocio]: (row) => this.movimientosocio(row, 2),
          [TableRowAction.Ledger]: (row) => this.mayor(row),
        },
        selectable: false,
        columns: [
          { key: 'nombre', label: 'Nombre', sortable: true, sortKey: 'nombre', onCellClick: (row) => this.edit(row) },
          { key: 'accountingModelsNumber', label: 'Cuenta contable', sortable: true, sortKey: 'accountingModelsNumber' },
          { key: 'ubicacion', label: 'Ubicación', sortable: true, sortKey: 'ubicacion', ellipsis: true },
          { key: 'fondoDeCaja', label: 'Fondo de caja', sortable: true, sortKey: 'fondoDeCaja', formatFn: (val) => Utils.FormatMoney(val ?? 0) + ' €' },
          {
            key: 'saldoContable', label: 'Saldo contable', sortable: true, sortKey: 'saldoContable',
            badge: {
              variantFn: (val: number) => (Number(val) || 0) < 0 ? 'danger' : ((Number(val) || 0) > 0 ? 'success' : 'neutral'),
              labelFn: (val: number) => Utils.FormatMoney(val ?? 0) + ' €',
            },
          },
        ],
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.create() },
        ],
        gridActions: [],
        rowConfig: {},
      },
      {
        filterView: '',
      }
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => { },
      onCollapse: () => { },
      onToggleExpand: () => { },
    };
  }

  ngAfterViewInit(): void {
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.summaryTemplate = this.cashSummaryTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        const filters: any = state.filters || {};
        const search = String(filters.search || '');
        if (search !== this.quickSearch) this.quickSearch = search;
        this.lastState = state;
        this.load();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  create(): void {
    CashFormDialogComponent.open(this.dialog, { uid: null })
      .subscribe(changed => { if (changed) this.load(); });
  }

  edit(row: CashRow): void {
    CashFormDialogComponent.open(this.dialog, { uid: row.uid, cash: row })
      .subscribe(changed => { if (changed) this.load(); });
  }

  delete(row: CashRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.confirm.open({
      title: 'Eliminar caja',
      subtitle: row.nombre || undefined,
      message: 'Se eliminará la caja junto con sus ingresos desde banco y movimientos de socio, y los asientos contables asociados. Si su cuenta contable no tiene más movimientos, también se eliminará. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.cashDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando caja…',
      errorText: 'Ha ocurrido un error al eliminar la caja/efectivo',
      action: () => this.efectivoService.delete(row.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Caja eliminada correctamente');
      this.load();
    });
  }

  ingresodebanco(row: CashRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    EfectivoDialogIngresoACajaComponent.open(this.dialog, { efectivo: row })
      .subscribe(changed => { if (changed) this.load(); });
  }

  movimientosocio(row: CashRow, tipoMovimientoSocioId: number): void {
    if (!this.tier.requireAdvancedTier()) return;
    EfectivoDialogMovimientoSocioComponent.open(this.dialog, { efectivo: row, tipoMovimientoSocioId })
      .subscribe(changed => { if (changed) this.load(); });
  }

  reload(): void {
    this.load();
  }

  mayor(row: CashRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    const accountNumber = row.accountingModelsNumber;
    if (!accountNumber) {
      this.notify.warning('Esta caja no dispone de cuenta contable asociada.');
      return;
    }
    this.loadingDialog.run('Abriendo mayor contable…', this.accountingAccountsService.list(0, 1, accountNumber))
      .then((x: any) => {
        let accountingModels: AccountingAccountsModel = new AccountingAccountsModel();
        const list = (x && x.data) as AccountingAccountsModel[];
        if (list && list.length === 1) accountingModels = list[0];
        this.dialog.open(AccountingAccountsDialogLedgerComponent, {
          width: '700px',
          maxWidth: '96vw',
          maxHeight: '90vh',
          autoFocus: false,
          data: {
            id: accountingModels.id,
            name: accountingModels.name,
            number: accountingModels.number,
          }
        });
      })
      .catch(error => {
        this.notify.error((error && error.error && error.error.message) || 'No se pudo abrir el mayor contable');
      });
  }

  private load(): void {
    if (!this.t1 || !this.lastState) return;
    const state = this.lastState;
    const filters: any = state.filters || {};
    const seq = ++this.loadSeq;
    this.t1.tableState.setLoading(true);

    this.reads.list({
      page: (state.pageIndex || 0) + 1,
      pageSize: state.pageSize || 10,
      search: String(filters.search || '').trim(),
      sortField: state.sortField || '',
      sortDirection: state.sortDirection || '',
    })
      .then(result => {
        if (seq !== this.loadSeq) return;
        const expanded = new Set(this.rows.filter(row => row.__expanded).map(row => row.uid));
        this.rows = result.items.map(item => ({ ...item, __expanded: expanded.has(item.uid) }));
        this.summary = result.summary;
        this.t1.tableState.setData(this.rows);
        this.t1.tableState.setTotalItems(result.total);
        this.t1.tableState.setLoading(false);
        this.cdr.markForCheck();
      })
      .catch(error => {
        if (seq !== this.loadSeq) return;
        this.t1.tableState.setLoading(false);
        this.notify.error((error && error.error && error.error.message) || 'No se pudo cargar la lista de cajas');
      });
  }

  private cashDetails(row: CashRow): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.accountingModelsNumber) details.push({ label: 'Cuenta contable', value: row.accountingModelsNumber });
    if (row.ubicacion) details.push({ label: 'Ubicación', value: row.ubicacion });
    details.push({ label: 'Saldo contable', value: Utils.FormatMoney(row.saldoContable ?? 0) + ' €' });
    return details;
  }
}
